import * as pinia from 'pinia';
import { useLocalStorage } from '@vueuse/core';
import dayjs from 'dayjs';
import axios from 'axios';
import { phasesData } from '@/Utils/configs';
import {
  PHASE_STATUS,
  VICE_SESSION_PHASES,
  USER_SESSION_ON_HOLD_KEY,
  SESSION_LOG_ENDPOINT,
} from '@/Utils/vice-constants';

const useSessionStore = pinia.defineStore('SessionStore', {
  state: () => ({
    debugMode: false,
    sessionDetails: null,
    isClinician: null,
    userIsObserver: null,
    vuzixStreamId: null,
    smartphoneStreamId: null,
    phases: phasesData,
    phasesDataInitialized: false,
    currentPhase: VICE_SESSION_PHASES.INTRODUCTION,
    isGroupCallStarted: false,
    isSessionEnded: false,
    isMyCameraOn: useLocalStorage('_vice:isMyCameraOn', false),
    isMyMicrophoneOn: useLocalStorage('_vice:isMyMicrophoneOn', false),
    navigatorUserId: '',
    isPhasePaused: false,
    togglingPausedState: false,
    isPutOnHold: localStorage.getItem(`${USER_SESSION_ON_HOLD_KEY}`) > 0,
    isOpenSidebar: useLocalStorage('_vice:isOpenSidebar', true),
    sidebarTab: useLocalStorage('_vice:sidebarTab', 'chatTab'),
    isConsultationCallStarted: false,
    screenshareOngoing: false,
    screenShares: {},
    hasPermissionError: false,
    consultationStreams: [],
    muted: [],
    jitsiCommands: null,
    authUser: null,
    deviceMessageSender: null,
    activeCallParticipants: {
      [VICE_SESSION_PHASES.INTRODUCTION]: {},
      [VICE_SESSION_PHASES.CONSULTATION]: {},
      [VICE_SESSION_PHASES.DEBRIEF]: {},
    },
    observerInteracted: false,
    hands: {},
    snapshots: {},
    snapshotsAllowed: false,
    isControlPanelOpened: false,
  }),

  getters: {
    focusMode: (state) => !!state.focusedStreamId,
    currentPhaseData: (state) => state.phases[state.currentPhase],
    shouldMuteAudio: (state) => state.isPutOnHold || state.isPhasePaused,
    allRemoteUsers: (state) => state.activeCallParticipants,
    mediaPermissionError: (state) => state.hasPermissionError && !state.userIsObserver,
    title: (state) => state.sessionDetails?.title,
    date: (state) => state.sessionDetails?.session_start_by_timezone,
    hasLivePhase: (state) =>
      Object.values(state.phases)
        .map((data) => data.status)
        .some((status) => status === PHASE_STATUS.LIVE),
    sessionId: (state) => state.sessionDetails?.unique_id?.replace(/-/g, ''),
    clinicianId: (state) => state.sessionDetails?.details?.clinician_id,
    sessionUniqueId: (state) => state.sessionDetails?.unique_id,
    sessionDBId: (state) => state.sessionDetails?.id,
    // Clinician in the introduction waiting phase has microphone on else revert to the cached
    // microphone state
    updatedMicrophoneState: (state) =>
      !state.hasLivePhase &&
      state.currentPhase === VICE_SESSION_PHASES.INTRODUCTION &&
      state.isClinician
        ? true
        : state.isMyMicrophoneOn,
    isHandRaised: (state) => (participantId) => state.hands?.[participantId]?.state === 'true',
    getHandRaisedTimestamp: (state) => (participantId) => state.hands[participantId]?.time,
  },

  actions: {
    addActiveCallParticipants(newRemoteUser) {
      const userId = Object.keys(newRemoteUser)[0];
      this.activeCallParticipants[this.currentPhase] = {
        ...this.activeCallParticipants[this.currentPhase],
        [userId]: {
          ...this.activeCallParticipants[this.currentPhase][userId],
          ...newRemoteUser[userId],
        },
      };
    },

    setHandRaised(userHand) {
      const userId = Object.keys(userHand)[0];
      const oldState = this.hands[userId];

      if (userHand[userId].time !== 0 && oldState?.time > userHand[userId].time) return;

      this.hands[userId] = { ...oldState, ...userHand[userId] };
    },

    addSnapshot(snapshot) {
      this.snapshots = { ...this.snapshots, [snapshot.id]: snapshot };
    },

    addPassedTimeFromStore() {
      if (!this.isPhasePaused) {
        const diffInSeconds = dayjs().diff(dayjs(this.currentPhaseData.lastTimeStamp), 'seconds');
        this.currentPhaseData.duration += diffInSeconds * 1000;
      }
    },

    updateCurrentPhase(value) {
      this.currentPhase = value;
    },

    setPhaseStatusToWaiting(phaseId) {
      this.phases[phaseId].status = PHASE_STATUS.WAITING;
    },

    setPhaseDurationToZero(phaseId) {
      this.phases[phaseId].duration = 0;
    },

    // Set duration to 0 if switching to previous phase occurs
    updatePhaseDuration(phaseId, duration, backToPreviousPhase) {
      this.phases[phaseId].duration = backToPreviousPhase ? 0 : duration;
    },
    toggleSidebar() {
      this.isOpenSidebar = !this.isOpenSidebar;
    },
    togglePausedState(shouldPause) {
      if (this.togglingPausedState) return;

      this.togglingPausedState = true;
      setTimeout(() => {
        this.togglingPausedState = false;
      }, 1000);
      this.isPhasePaused = shouldPause;
    },

    checkForPermissions() {
      const recheckPermissions = _.debounce(this.checkForPermissions, 1000);

      navigator.permissions
        .query({ name: 'camera' })
        .then((permissionStatus) => {
          if (permissionStatus.state === 'granted') {
            console.log('%c Media permission granted! ', 'color: green');
            recheckPermissions.cancel();
            this.hasPermissionError = false;
          } else if (permissionStatus.state === 'prompt') {
            console.log('%c Media permission prompt! ', 'color: orange');
            this.hasPermissionError = true;
          } else if (permissionStatus.state === 'denied') {
            console.log('%c Media permission denied! ', 'color: red');
            this.hasPermissionError = true;
            recheckPermissions();
          }

          this.logUserMediaPermissionFired({
            status: `Permission ${permissionStatus.state.toUpperCase()}`,
          });
        })
        .catch((error) => {
          console.error(error);
        });
    },

    logUserMediaPermissionFired(data) {
      try {
        axios.post(
          SESSION_LOG_ENDPOINT,
          {
            message: `UserMediaPermissions: [SessionID: ${this.sessionUniqueId}, User: ${this.authUser.name} (${this.authUser.id})]`,
            data,
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );
      } catch (error) {
        console.error(error.message);
      }
    },

    toggleControlPanel(state = null) {
      if (state !== null) {
        this.isControlPanelOpened = state;
      } else {
        this.isControlPanelOpened = !this.isControlPanelOpened;
      }
    },
  },
});
export default useSessionStore;
